import { AddressableViewLayout } from 'framework/ViewTypes';
import { ObjectDetailsContentLayout } from 'framework/ViewTypes/contentLayout';
import { ObjectDetailsSections } from 'framework/ViewTypes/embeddable';
import ApplicationActiveProcessesPanel from './ApplicationActiveProcessesPanel';
import ApplicationActivityTabs from './ApplicationActivityTabs';
import ApplicationSummarySection from './sections/ApplicationSummarySection';
import CandidateSection from './sections/CandidateSection';
import ContactInformationSection from './sections/ContactInformationSection';
import PipelineSection from './sections/PipelineSection';
import useApplicationDetailsData from './useApplicationDetailsData';
import { getApplicationStageConfig, hasRejectionDecision } from './stageConfig';
import { getApplicationDrawers } from '../shared/drawers';
import useApplicationActions from '../shared/useApplicationActions';

const ApplicationDetailsPage = () => {
  const { application, isLoading, error, reload } = useApplicationDetailsData();
  const { getActions, drawerProps } = useApplicationActions();

  const renderContent = (loadedApplication: NonNullable<typeof application>) => {
    const stageConfig = getApplicationStageConfig(loadedApplication.stage, getActions(loadedApplication));
    const checks = stageConfig.getChecks?.(loadedApplication) ?? [];
    const processesConfig = stageConfig.getActiveProcessesProps(loadedApplication);

    const sections = [
      { title: 'Application details', content: <ApplicationSummarySection application={loadedApplication} /> },
      { title: 'Candidate', content: <CandidateSection application={loadedApplication} /> },
      { title: 'Contact information', content: <ContactInformationSection application={loadedApplication} /> },
      {
        title: 'Pipeline',
        content: (
          <PipelineSection
            stage={loadedApplication.stage}
            rejectedFrom={loadedApplication.rejectedFrom}
            isRejected={hasRejectionDecision(loadedApplication)}
            checks={checks}
          />
        ),
      },
    ];

    return {
      header: {
        title: `${loadedApplication.candidateName} / ${loadedApplication.jobPostingName}`,
        badges: [stageConfig.badge],
        attributes: stageConfig.getAttributes(loadedApplication),
        actions: stageConfig.getActions(loadedApplication),
      },
      leftSide: <ObjectDetailsSections sections={sections} />,
      rightSide: [
        ...(processesConfig.items.length ? [ApplicationActiveProcessesPanel(processesConfig)] : []),
        <ApplicationActivityTabs
          timeline={loadedApplication.timeline}
          artifacts={loadedApplication.artifacts}
          offers={loadedApplication.offers ?? []}
        />,
      ],
    };
  };

  return (
    <AddressableViewLayout
      loading={isLoading}
      drawer={application ? getApplicationDrawers(drawerProps) : undefined}
      contentLayout={
        <ObjectDetailsContentLayout
          data={application}
          loading={isLoading}
          error={error}
          retry={reload}
          notFoundMessage="Application not found."
          renderContent={renderContent}
        />
      }
    />
  );
};

export default ApplicationDetailsPage;
